import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { ListTableComponent } from '@ppl/ui';
import { DatePickerModule } from 'primeng/datepicker';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { PopupDialogComponent } from '@ppl/ui';
import { ButtonComponent } from '@ppl/ui';
import { ENDPOINTS } from '../../../../shared/constants/url.constants';
import { TimesheetService } from '../../../../services/timesheet.service';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { environment } from '../../../../../environments/environment';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { DateTimeFormatterService } from 'src/app/shared/utils/date-time-formatter.service';
import { ErrorAlertService } from 'src/app/shared/services/error-alert.service';
import { ListService } from 'src/app/shared/services/list.service';
import { LookupService } from 'src/app/services/lookup.service';
import { UserService } from 'src/app/services/user.service';
import { TableRowActions } from 'src/app/shared/interfaces/actions';
import { TableRowActionsModel } from 'src/app/shared/interfaces/list.interface';
import { LookupValueItem } from 'src/app/interfaces/lookup.interface';
import { InvoiceFilterComponent } from '../staff/invoices-staff-list/invoice-filter/invoice-filter.component';
import { InvoiceService } from 'src/app/services/invoice.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { AuthorizationDetailsComponent } from '../../authorizations/authorization-details/authorization-details.component';
import { SectionsService } from 'src/app/services/sections.service';
import { AddInvoiceComponent } from '../provider/provider-invoice/add-invoice/add-invoice.component';
import { EditpdfComponent } from '../../../editpdf/editpdf.component';
import { PDFFormConfig } from 'src/app/interfaces/config';

@Component({
  selector: 'ppl-invoice-request-payroll-adjustment-approvals',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgZorroModule,
    NzDatePickerModule,
    DatePickerModule,
    ButtonComponent,
    PopupDialogComponent,
    InvoiceFilterComponent,
    ListTableComponent,
    AuthorizationDetailsComponent,
    AddInvoiceComponent,
    EditpdfComponent,
  ],
  templateUrl: './invoice-request-payroll-adjustment-approvals.component.html',
  styleUrl: './invoice-request-payroll-adjustment-approvals.component.css'
})
export class InvoiceRequestPayrollAdjustmentApprovalsComponent {
  @Input() source: string = 'invoice';
  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
  @ViewChild(InvoiceFilterComponent) filterChildComponent!: InvoiceFilterComponent;
  @ViewChild('searchFilterWarningDialog') searchFilterWarningDialog!: TemplateRef<any>;
  @ViewChild('authDialog') authDialog!: TemplateRef<any>;
  // Passed to ppl-authorization-details as an input; never put into the shared selected lead.
  selectedAuthorizationId: any = null;
  @ViewChild('viewInvoiceDialog') viewInvoiceDialog!: TemplateRef<any>;
  @ViewChild('pdfDialog') pdfDialogRef!: TemplateRef<any>;

  private dialogSvc = inject(DialogService);
  private allowListStore = inject(AllowListAuthStore);

  pageId = PAGE_CONSTANTS.STAFF_INVOICE_ADJUSTMENT_APPROVALS;
  isReadOnly = false;
  isPageRestrictionLoaded = false;

  actionButtons: TableRowActionsModel = new TableRowActions({ view: true });
  mode: 'add' | 'edit' | 'view' | 'actions' = 'view';
  invoiceId: string = '';
  selectedLineItem: any;
  viewInvoiceDialogId: string = '';
  appliedFilters: any = [];
  approvalTimesheetForm!: FormGroup;
  dialogButtonLabel = '';
  commentText = '';
  dialogData: any;
  dialogNoteMessage = '';
  isDialogNoteDisplayed = false;
  programName!: string;
  processingStatusList: any;
  reasonList: any;
  businessStatusList: any;
  selectedAction: any;
  serviceCodes: LookupValueItem[] = [];
  showActionPopup: boolean = false;
  timesheets: any[] = [];
  hasSearched = false;
  // Toggled to destroy + recreate the results table for a clean full refresh on search.
  listReloading = false;
  title = '';
  chequeNumbers: any[] = [];
  pdfConfig?: PDFFormConfig;
  pdfFile?: string;
  pdfBlob?: Blob;
  showModal = false;
  modalBody = {
    pdfViewer: ''
  };

  buttons = [
    {
      button_id: "Approve",
      button_label: "Approve",
      buttonAction: "approveTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "ApproveAll",
      button_label: "Approve All",
      buttonAction: "approveAllTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "Reject",
      button_label: "Reject",
      buttonAction: "rejectTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "RejectAll",
      button_label: "Reject All",
      buttonAction: "rejectAllTimesheets",
      className: "primary-btn-color"
    }
  ];

  cols = [
    { field: 'serviceDate', header: 'Service \nDate', jsonpath: 'serviceDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'serviceCodeWithDesc', header: 'Service \nCode', jsonpath: 'serviceCodeWithDesc', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'authDesc', header: 'Service \nDescription', jsonpath: 'authDesc', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'consumerName', header: 'Participant \nName', jsonpath: 'consumerName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'consumerPplId', header: 'Participant \nPPL ID', jsonpath: 'consumerPplId', isDisplayedAtHeader: 'y', isSearchable: 'n', isClickable: true },
    { field: 'providerName', header: 'Provider \nName', jsonpath: 'providerName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'providerPplId', header: 'Provider \nPPL ID', jsonpath: 'providerPplId', isDisplayedAtHeader: 'y', isSearchable: 'n', isClickable: true },
    { field: 'invoiceNumber', header: 'Invoice \nNumber', jsonpath: 'invoiceNumber', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'invoiceDate', header: 'Invoice \nDate', jsonpath: 'invoiceDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'billedUnits', header: 'Billed \nUnits', jsonpath: 'billedUnits', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'paidUnits', header: 'Paid \nUnits', jsonpath: 'paidUnits', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'unitPrice', header: 'Unit \nPrice', jsonpath: 'unitPrice', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'totalCost', header: 'Total \nCost', jsonpath: 'totalCost', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'businessStatus', header: 'Business \nStatus', jsonpath: 'businessStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'processingStatus', header: 'Processing \nStatus', jsonpath: 'processingStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    // { field: 'sourceTypeLkp', header: 'Source', jsonpath: 'sourceTypeLkp', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'mcoName', header: 'Regional \nCenter', jsonpath: 'mcoName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'tasAuthorizationDetailId', header: 'Authorization \nNumber', jsonpath: 'tasAuthorizationDetailId', isDisplayedAtHeader: 'y', isSearchable: 'n', isClickable: true },
    { field: 'chequeNumber', header: 'Check \nNumber', jsonpath: 'chequeNumber', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  ];

  // lineItemCols = [
  //   {
  //     field: 'createdDate',
  //     header: 'Date',
  //     jsonpath: 'createdDate',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'authDesc',
  //     header: 'Service Description',
  //     jsonpath: 'authDesc',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'consumerName',
  //     header: 'Participant Name',
  //     jsonpath: 'consumerName',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'y',
  //   },
  //   {
  //     field: 'consumerPplId',
  //     header: 'Participant PPL ID',
  //     jsonpath: 'consumerPplId',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'providerName',
  //     header: 'Provider Name',
  //     jsonpath: 'providerName',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'providerPplId',
  //     header: 'Provider PPL ID',
  //     jsonpath: 'providerPplId',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },

  //   {
  //     field: 'timeInTimeOut',
  //     header: 'Time In - Time Out',
  //     jsonpath: 'timeInTimeOut',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'billedUnits',
  //     header: 'Billed Units (hrs)',
  //     jsonpath: 'billedUnits',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'paidUnits',
  //     header: 'Paid Units',
  //     jsonpath: 'paidUnits',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'serviceRate',
  //     header: 'Service Rate',
  //     jsonpath: 'serviceRate',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'businessStatus',
  //     header: 'Business Status',
  //     jsonpath: 'businessStatus',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'processingStatus',
  //     header: 'Processing Status',
  //     jsonpath: 'processingStatus',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   }
  // ];

  // Handler for list-table view user events (clicking PPL IDs)
  onViewUserClicked = (payload: any) => {
    this.userService.setViewUserEvent({
      userId: payload.userId,
      isConsumer: payload.isConsumer
    });
  }

  fields = [
    { field: 'Timeframe', show: true, default: '30 days' },
    { field: 'serviceDate', show: true, hideFuturePeriods: true },  // ← add this
    { field: 'consumer', show: true },
    { field: 'consumerPplId', show: true },
    { field: 'provider', show: true },
    { field: 'providerPplId', show: true },
    { field: 'serviceCode', show: true },
    { field: 'servicePeriod', show: true, hideFuturePeriods: true },
    { field: 'payPeriod', show: true, hideFuturePeriods: true },
  ];

  keyMapping: { [key: string]: string } = {
    serviceDate: 'serviceDate',
    Timeframe: 'serviceDate',
    consumer: 'consumerName',
    consumerPplId: 'consumerPplId',
    provider: 'providerName',
    providerPplId: 'providerPplId',
    serviceCode: 'serviceCode',
    servicePeriod: 'servicePeriodId',
    payPeriod: 'payPeriodId'
  };

  constructor(
    private dateTimeFormatterService: DateTimeFormatterService,
    private errorAlertService: ErrorAlertService,
    private fb: FormBuilder,
    private listService: ListService,
    private lookupService: LookupService,
    private timesheetService: TimesheetService,
    private userService: UserService,
    private invoiceService: InvoiceService,
    private notificationService: NotificationService,
    private sectionsService: SectionsService
  ) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.businessStatusList = this.lookupService.getLookupValue('Business Status');
    this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
    this.reasonList = this.lookupService.getLookupValue('Disposition Reason');
    this.serviceCodes = this.lookupService.getLookupValue('timesheet_filter_serviceCode');

    this.approvalTimesheetForm = this.fb.group({
      comments: [null]
    });

    this.setPageAccess();
  }

  private setPageAccess() {
    const authConfig = this.allowListStore.getConfig();
    if (authConfig && !authConfig.pages[this.pageId]) {
      this.isReadOnly = true;
    }
    this.isPageRestrictionLoaded = true;
  }


  searchTimesheets(filters: any, startingPageNumber = 1, currentPage = 0, isChequeLookUp = false, recordsPerPage = 20): void {
    this.hasSearched = true;

    const adjustedTimesheetPayload = this.buildRequestPayload(
      'invoiceAdjustmentEntry',
      'desc',
      filters,
      startingPageNumber,
      recordsPerPage
    );

    this.listService.getListData(adjustedTimesheetPayload, 'claims').subscribe((data: any) => {
      const records = this.processData(data.responsedata);
      if (isChequeLookUp) {
        if (records?.length) {
          this.chequeNumbers = Array.from(
            new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
        }
      } else {
        this.timesheets = startingPageNumber > 1
          ? [...this.timesheets, ...records]
          : records;
        if (this.timesheets?.length) {
          this.chequeNumbers = Array.from(
            new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
        }
      }
    });
  }

  searchSubRowTimesheets(filters: any, event: any, startingPageNumber = 1, currentPage = 0) {
    const adjustedTimesheetPayload = this.buildRequestPayload(
      'invoiceAdjustmentEntry',
      'invoiceLineId',
      filters,
      startingPageNumber
    );

    this.listService
      .getListData(adjustedTimesheetPayload, 'claims')
      .subscribe((data: any) => {
        event.lineItems = data.responsedata;

        event?.lineItems?.forEach((element: any) => {
          element.timeInTimeOut = this.dateTimeFormatterService.convertTo12HourFormat(element.timeIn) + ' - ' + this.dateTimeFormatterService.convertTo12HourFormat(element.timeOut),
            element.businessStatus = this.businessStatusList.filter((status: any) => status.lkpValueCode === element.businessStatusLkp)
              .map((status: any) => status.lkpValueName),
            element.processingStatus = this.processingStatusList.filter((status: any) => status.lkpValueCode === element.processingStatusLkp)
              .map((status: any) => status.lkpValueName)
        });

        event.showLineItem = true;
        event.loaded = true;
      });
  }

  buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1, recordsPerPage = 20) {
    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: [],
      orderBy: ['desc'],
    };

    return payload;
  }

  processData(data: any): any {
    if (data?.length > 0) {
      data.forEach((element: any) => {
        element.timeInTimeOut =
          this.dateTimeFormatterService.convertTo12HourFormat(element.timeIn) +
          ' - ' +
          this.dateTimeFormatterService.convertTo12HourFormat(element.timeOut);

        element.businessStatus = this.businessStatusList
          .filter((s: any) => s.lkpValueCode === element.businessStatusLkp)
          .map((s: any) => s.lkpValueName);

        element.processingStatus = this.processingStatusList
          .filter((s: any) => s.lkpValueCode === element.processingStatusLkp)
          .map((s: any) => s.lkpValueName);

        element.serviceCodeWithDesc = [element.serviceCode, element.serviceDescription]
          .filter((part: any) => part != null && String(part).trim() !== '')
          .join(' - ');
      });
    }
    return data;
  }

  onCellClicked(event: any): void {
    const col = event?.col;
    const rowData = event?.rowData;
    if (col?.field === 'tasAuthorizationDetailId' && rowData?.tasAuthorizationDetailId) {
      this.openAuthDialog(rowData);
    }
  }

  onAction(event: any): void {
    if (event.action === 'view') {
      this.selectedLineItem = event.data;
      this.invoiceId = event.data.invoiceHeaderId;
      this.onView();
    }
  }

  onView(): void {
    this.openInvoice('view');
  }

  onRowActions(event: any): void {
    if (event.type === 'download') {
      this.timesheetService.downloadPaystub(event, this.programName);
    } else {
      this.viewPaystub(event, this.programName);
    }
  }

  viewPaystub(event: any, programCode: string): void {
    this.showModal = true;
    this.modalBody.pdfViewer = 'Paystub';

    this.pdfConfig = {
      form_id: '',
      is_mandatory: false,
      form_description: '',
      form_name: '',
      fields: []
    };

    const dialogConfig: DialogConfig = {
      title: this.modalBody.pdfViewer,
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(this.pdfDialogRef, dialogConfig, () => this.handlePDFDialogCancel(), () => this.handlePDFDialogCancel());

    this.getPaystub(event.event.checkId, event.event.chequeNumber, event.event.providerPplId, programCode);
  }

  getPaystub(checkId: string, chequeNumber: string, empId: string, programCode: string): void {
    this.timesheetService.getPayStub(checkId, chequeNumber, empId, programCode).subscribe((response: HttpResponse<Blob>) => {
      if (response.body) {
        const blob = new Blob([response.body], { type: response.body.type });
        this.pdfBlob = blob;
        this.pdfFile = window.URL.createObjectURL(blob);
      }
    });
  }

  handlePDFDialogCancel(): void {
    this.selectedLineItem = undefined;
    this.showModal = false;
    this.pdfConfig = undefined;
    this.pdfFile = undefined;
  }

  fieldsUpdated(event: any): void {
  }

  openInvoice(mode: 'add' | 'edit' | 'view' | 'actions'): void {
    this.mode = mode;
    const dialogConfig: DialogConfig = {
      title: 'View Invoice',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.viewInvoiceDialogId = this.dialogSvc.openDialog(
      this.viewInvoiceDialog,
      dialogConfig,
      () => this.handleViewCancel({ isCloseTriggeredByDialog: true }),
      () => this.handleViewCancel({ isCloseTriggeredByDialog: true })
    );
  }

  handleViewCancel(event: any): void {
    if ((Array.isArray(event) && event[0]?.invoiceHeaderId) || event?.isCloseTriggeredByDialog) {
      this.dialogSvc.closeDialog(this.viewInvoiceDialogId);
      this.invoiceId = '';
      this.selectedLineItem = null;
    }
  }

  openAuthDialog(rowData: any): void {
    this.selectedAuthorizationId = rowData.tasAuthorizationDetailId;
    const dialogConfig: DialogConfig = {
      title: `Auth Details for ${rowData.tasAuthorizationDetailId}`,
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };
    this.dialogSvc.openDialog(this.authDialog, dialogConfig, () => { }, () => { });
  }

  onSearch(event: any): void {
    const filters: any[] = [];

    if (event.Timeframe) {
      filters.push({
        jsonPath: 'serviceDate',
        value: event.Timeframe,
        operator: 'greaterThan'
      });
    }

    if (event.serviceDate) {                                          // ← add this block
      filters.push({
        jsonPath: 'serviceDate',
        value: this.dateTimeFormatterService.formatDateToString(event.serviceDate as Date),
        operator: 'Equal'
      });
    }
    if (event.consumer?.trim()) {
      filters.push({ jsonPath: 'consumerName', value: event.consumer.trim(), operator: 'equal' });
    }
    if (event.consumerPplId?.trim()) {
      filters.push({ jsonPath: 'consumerPplId', value: event.consumerPplId.trim(), operator: 'Equal' });
    }
    if (event.provider?.trim()) {
      filters.push({ jsonPath: 'providerName', value: event.provider.trim(), operator: 'equal' });
    }
    if (event.providerPplId?.trim()) {
      filters.push({ jsonPath: 'providerPplId', value: event.providerPplId.trim(), operator: 'Equal' });
    }
    if (event.serviceCode) {
      filters.push({ jsonPath: 'serviceCode', value: event.serviceCode, operator: 'Equal' });
    }
    if (event.servicePeriod) {
      filters.push({ jsonPath: 'servicePeriodId', value: event.servicePeriod, operator: 'Equal' });
    }
    if (event.payPeriod) {
      filters.push({ jsonPath: 'payPeriodId', value: event.payPeriod, operator: 'Equal' });
    }

    filters.push({
      jsonPath: "processingStatusLkp",
      values: ['VI', 'AI'],
      operator: "IN"
    });
    this.appliedFilters = filters;
    this.reloadGrid(filters);
  }

  // Destroys + recreates the results table so the lazy loadPage grid fully resets to the
  // latest filter results, then reloads.
  private reloadGrid(filters: any): void {
    this.listReloading = true;
    this.timesheets = [];
    setTimeout(() => {
      this.listReloading = false;
      this.searchTimesheets(filters);
    });
  }

  onPageLoad(event: any): void {
    this.searchTimesheets(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, false, event.rows ?? 20);
  }

  openWarningMessage() {
    const dialogConfig: DialogConfig = {
      title: 'Error',
      showOkButton: true,
      okText: 'Ok',
      size: 'medium',
      position: 'top',
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.searchFilterWarningDialog,
      dialogConfig,
      () => { },
      () => { }
    );
  }

  rowExpanded(event: any) {
    if (event.loaded) {
      event.showLineItem = !event.showLineItem;
      return;
    }

    const subRowFilter = [];
    subRowFilter.push({
      jsonPath: "parent_timesheetLineId",
      value: event.timesheetLineId,
      operator: "Equal"
    });

    this.searchSubRowTimesheets(subRowFilter, event);
  }

  areArraysEqual(a: any[], b: any[]): boolean {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((val, i) => val === b[i]);
  }

  isFilterValid(timesheetFilterValues: any, searchCallbackFilterValues: any): boolean {
    const activeFilters = Object.keys(timesheetFilterValues).filter(key => {
      const value = timesheetFilterValues[key];
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    });

    const userAppliedFilters = searchCallbackFilterValues.filter(
      (filter: any) => !['businessStatusLkp', 'processingStatusLkp', 'serviceDuration'].includes(filter.jsonPath)
    );

    if (activeFilters.length !== userAppliedFilters.length) return false;

    return Object.entries(timesheetFilterValues)
      .filter(([_, value]) => value !== null && value !== '')
      .every(([key, value]: [any, any]) => {
        const mappedKey = this.keyMapping[key] || key;
        if (Array.isArray(value) && value.length === 0) return true;

        let comparisonValue: any;
        if (key === 'Timeframe') {
          comparisonValue = value;
        }
        else if (mappedKey === 'serviceDate') {
          comparisonValue = this.dateTimeFormatterService.formatDateToString(value as Date);
        } else if (mappedKey === 'serviceDuration') {
          comparisonValue = value.substr(0, value.indexOf(' '));
        } else {
          comparisonValue = value;
        }

        return searchCallbackFilterValues.some((filter: any) =>
          filter.jsonPath === mappedKey &&
          (Array.isArray(filter.value || filter.values)
            ? this.areArraysEqual(filter.values, comparisonValue)
            : typeof filter?.value === 'string'
              ? filter?.value?.trim() === comparisonValue?.trim()
              : filter?.value === comparisonValue)
        );
      });
  }

  gridAction(event: any) {
    if (this.isReadOnly) return;
    const { button, selectedCheckBox } = event;

    if (!this.isFilterValid(this.filterChildComponent.invoiceSearchForm.value, this.appliedFilters)) {
      const errorMessage = `Some filters are newly applied but the search is not performed. Kindly perform search with updated filter or remove the filter.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (this.appliedFilters.length === 0 && (button.buttonAction === 'approveAllTimesheet' || button.buttonAction === 'rejectAllTimesheets')) {
      const errorMessage = `${button.button_label} action requires selection of filters. Kindly apply atleast one filter and perform the search with updated filter.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (selectedCheckBox && selectedCheckBox.length === 0
      && (button.buttonAction === 'approveTimesheet' || button.buttonAction === 'rejectTimesheet')) {
      const errorMessage = `Kindly select checkbox and then perform this action.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (selectedCheckBox.length > 1 && (button.buttonAction === 'approveTimesheet' || button.buttonAction === 'rejectTimesheet')) {
      const errorMessage = `Approve / Reject operation can be performed on single timesheet at a time.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (button) {
      this.dialogData = { payload: {} };
      switch (button.buttonAction) {
        case 'approveTimesheet':
        case 'approveAllTimesheet': {
          const selectedTimesheet = selectedCheckBox[0];
          this.dialogData.payload.invoiceLineIds = [selectedTimesheet.invoiceLineId];
          this.isDialogNoteDisplayed = false;
          break;
        }

        case 'rejectTimesheet':
        case 'rejectAllTimesheets': {
          if (selectedCheckBox.length > 0) {
            this.dialogData.payload.invoiceLineIds = selectedCheckBox.map((entry: any) => entry.invoiceLineId);
            this.dialogNoteMessage = `Note: The ${button.button_label} action will be performed on all selected invoices.`
          }
          this.isDialogNoteDisplayed = true;
          break;
        }
      }

      this.dialogData.button = button;
      this.dialogButtonLabel = button.button_label;
      this.title = `${this.source.trim().charAt(0).toUpperCase() + this.source.trim().slice(1)} Adjustment ${button.button_label}`;
      this.commentText = button.button_label.indexOf('Approve') > -1 ? 'Approval comment' : 'Rejection comment *';

      if (button.buttonAction === 'rejectTimesheet' || button.buttonAction === 'rejectAllTimesheets') {
        this.approvalTimesheetForm.get('comments')?.setValidators([Validators.required]);
      } else {
        this.approvalTimesheetForm.get('comments')?.setValidators(null);
      }
      this.approvalTimesheetForm.get('comments')?.setValue(null);
      this.approvalTimesheetForm.markAsUntouched();
      this.approvalTimesheetForm.get('comments')?.updateValueAndValidity();
      this.showActionPopup = true;
    }
  }

  onSave() {
    if (this.isReadOnly) return;
    if (this.approvalTimesheetForm.valid) {
      let placeholder = '';
      const urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_INVOICE_DECISION
      const payload = this.dialogData.payload;
      payload.comments = this.approvalTimesheetForm?.get('comments')?.value;

      switch (this.dialogData.button.buttonAction) {
        case 'approveTimesheet':
        case 'approveAllTimesheet': {
          payload.decision = 'A';
          placeholder = 'Invoce approved';
          break;
        }
        case 'rejectTimesheet':
        case 'rejectAllTimesheets': {
          payload.decision = 'R';
          placeholder = 'Invoce rejected';
          break;
        }
      }

      if (urlEndpoint) {
        this.invoiceService.adjustInvoceRequest(urlEndpoint, this.programName, payload, placeholder)
          .subscribe({
            next: () => {
              this.dialogData = null;
              this.showActionPopup = false;
              this.approvalTimesheetForm.reset();
              this.searchTimesheets(this.appliedFilters);
            },
            error: (err) => {
              this.notificationService.error('Failed', 'Failed')
            }
          });
      }
    } else {
      this.approvalTimesheetForm.markAllAsTouched();
    }
  }

}
